/**
 * Convert a breakpoint name/pixel value object into a pixel range version
 *
 * Breakpoints should be defined from smallest to largest!
 *
 * @param {object} breakpoints `{ [String]: Number }`;
 *
 * @return {array} [{ name: String, min: Number, max: Number }]
 */
export function getBreakpointRanges(breakpoints) {
	const bps = Object.entries(breakpoints);
	return bps.map(([name, pixels], i) => {
		// A maximum will always exist except for our final breakpoint
		const hasMax = i < bps.length - 1;
		const maxPixels = hasMax ? bps[i + 1][1] - 1 : undefined;
		return { name, min: pixels, max: maxPixels };
	});
}

/**
 * Creates media queries based on the breakpoints provided.
 *
 * Breakpoints should be defined from smallest to largest!
 *
 * ```
 * const breakpoints = {
 *   mobile: 320,
 *   tablet: 960,
 *   desktop: 1024
 * };
 * ```
 *
 * These are compositional for usage within your Emotion based components.
 *
 */
export function constructMediaQueries(breakpoints) {
	const ranges = getBreakpointRanges(breakpoints);

	return ranges.reduce(
		(acc, bp) => {
			const { name, min, max } = bp;

			acc.from[name] = `@media (min-width: ${min}px)`;
			acc.until[name] = `@media (max-width: ${min - 1}px)`;
			acc.only[name] = `@media (min-width: ${min}px) and (max-width: ${max}px)`;

			return acc;
		},
		{ from: {}, until: {}, only: {} }
	);
}
